import React, { useEffect, useState } from 'react';
import { Play, Copy, Download, RotateCcw } from 'lucide-react';
import PythonGuideModal from './PythonGuideModal';

interface PythonCodeBlockProps {
  children: string;
  filename?: string;
  editable?: boolean;
  title?: string;
}

declare global {
  interface Window {
    loadPyodide: any;
    pyodide: any;
  }
}

export const InteractivePythonBlock: React.FC<PythonCodeBlockProps> = ({
  children,
  filename = 'script.py',
  editable = true,
  title,
}) => {
  const initialCode = React.useMemo(() => {
    try {
      if (typeof children === 'string') {
        return children.trim();
      }
      return String(children || '').trim();
    } catch (error) {
      console.warn('Error processing children prop:', error);
      return '';
    }
  }, [children]);

  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [pyodideReady, setPyodideReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const initPyodide = async () => {
      try {
        if (window.pyodide) {
          setPyodideReady(true);
          return;
        }

        if (document.querySelector('script[src*="pyodide"]')) {
          const checkReady = setInterval(() => {
            if (window.pyodide) {
              setPyodideReady(true);
              clearInterval(checkReady);
            }
          }, 100);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js';
        script.onload = async () => {
          try {
            const pyodide = await window.loadPyodide({
              indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/',
            });

            await pyodide.loadPackage(['numpy', 'pandas', 'matplotlib']);

            window.pyodide = pyodide;
            setPyodideReady(true);
          } catch (error) {
            console.error('Pyodide init error:', error);
          }
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('Pyodide setup error:', error);
      }
    };

    initPyodide();
  }, []);

  const runCode = async () => {
    if (!pyodideReady || !window.pyodide) {
      setOutput('Python environment not ready yet...');
      return;
    }

    setIsRunning(true);
    setOutput('Running...');

    try {
      window.pyodide.runPython(`
import sys
from io import StringIO
sys.stdout = StringIO()
sys.stderr = StringIO()
`);

      window.pyodide.runPython(code);

      const stdout = window.pyodide.runPython('sys.stdout.getvalue()');
      const stderr = window.pyodide.runPython('sys.stderr.getvalue()');

      if (stderr) {
        setOutput(`Error: ${stderr}`);
      } else {
        setOutput(stdout || 'Code executed successfully (no output)');
      }
    } catch (error: any) {
      setOutput(`Error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const downloadCode = () => {
    const blob = new Blob([code], { type: 'text/python' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const resetCode = () => {
    setCode(initialCode);
    setOutput('');
  };

  return (
    <>
      <style>{`
        .python-force-white {
          background-color: #000000 !important;
          color: #ffffff !important;
          font-family: 'Monaco', 'Consolas', monospace !important;
        }
        .python-force-white * {
          background-color: #000000 !important;
          color: #ffffff !important;
        }
      `}</style>
      <div
        className="interactive-python-block my-6 border border-green-500 rounded-lg overflow-hidden bg-black"
        data-python-block
      >
        <div className="bg-gray-900 px-4 py-2 border-b border-green-500">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-green-400">
                {title || filename}
              </span>
              <span className="bg-green-600 text-black px-2 py-1 rounded text-xs font-bold ml-2">Python</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={copyCode}
                className="p-1 text-green-400 hover:text-green-300 transition-colors"
                title="Copy code"
              >
                <Copy size={16} />
              </button>
              <button
                onClick={downloadCode}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="Download code"
              >
                <Download size={16} />
              </button>
              <button
                onClick={resetCode}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="Reset code"
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={runCode}
                disabled={!pyodideReady || isRunning}
                className="hidden items-center space-x-1 rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:flex"
              >
                <Play size={14} />
                <span>{isRunning ? 'Running...' : 'Run'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sm:hidden">
          <button
            onClick={runCode}
            disabled={!pyodideReady || isRunning}
            className="flex w-full items-center justify-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play size={16} />
            <span>{isRunning ? 'Running...' : 'Run Code'}</span>
          </button>
        </div>

        <div className="relative">
          <textarea
            value={code}
            onChange={(event) => editable && setCode(event.target.value)}
            className="python-force-white"
            style={{
              width: '100%',
              height: '16rem',
              padding: '1rem',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: 1.5,
              backgroundColor: '#000000',
              color: '#ffffff',
              resize: 'none',
              outline: 'none',
              border: '2px solid #22c55e',
            }}
            readOnly={!editable}
            spellCheck={false}
          />
        </div>


        {output && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Output:</span>
            </div>
            <pre
              className="python-force-white"
              style={{
                padding: '1rem',
                backgroundColor: '#000000',
                color: '#ffffff',
                fontSize: '14px',
                overflowX: 'auto',
                maxHeight: '16rem',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                margin: 0,
                whiteSpace: 'pre-wrap',
              }}
            >
              {output}
            </pre>
          </div>
        )}

        <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>{pyodideReady ? 'Python Ready' : 'Loading Python...'}</span>
            <div className="flex items-center gap-3">
              <span>Editable: {editable ? 'Yes' : 'No'}</span>
              <button
                onClick={runCode}
                disabled={!pyodideReady || isRunning}
                className="flex items-center space-x-1 rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play size={14} />
                <span>{isRunning ? 'Running...' : 'Run Code'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <PythonGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        moduleTitle={title || filename}
      />
    </>
  );
};
